import { C, font } from "../../styles/tokens";
import { formatDobToDmy } from "../../utils/date";
import { InlineIcon } from "../InlineIcon";

export function VerificationStatus({
  status,
  type,
  referenceId,
  verifiedAt,
  details,
}) {
  if (!status || status === "PENDING" || status === "NONE") {
    return (
      <div style={containerStyle("pending")}>
        <div style={badgeStyle("pending")}>Verification Pending</div>
        <div style={textStyle}>
          Account identity verification ({type}) has not been completed.
        </div>
      </div>
    );
  }

  if (status === "VERIFIED") {
    const isAadhaar = type === "AADHAAR";
    const isPan = type === "PAN";

    return (
      <div style={containerStyle("verified")}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <div style={badgeStyle("verified")}>
            <InlineIcon name="check" size={12} />{" "}
            {isAadhaar
              ? "Aadhaar Verification Successful"
              : isPan
                ? "PAN Verification Successful"
                : "GST Verification Successful"}
          </div>
          <span style={secureBadgeStyle}>Verified Identity</span>
        </div>

        {isAadhaar ? (
          <div
            style={{
              display: "grid",
              gap: 6,
              fontSize: 12,
              borderTop: "1px solid #dcfce7",
              paddingTop: 8,
            }}
          >
            <div style={rowStyle}>
              <span style={labelStyle}>Full Name:</span>
              <span style={valueStyle}>
                {details?.name || details?.fullName || "Verified Citizen"}
              </span>
            </div>
            {details?.dateOfBirth && (
              <div style={rowStyle}>
                <span style={labelStyle}>Date of Birth:</span>
                <span style={valueStyle}>
                  {formatDobToDmy(details.dateOfBirth)}
                </span>
              </div>
            )}
            {details?.gender && (
              <div style={rowStyle}>
                <span style={labelStyle}>Gender:</span>
                <span style={valueStyle}>
                  {details.gender === "M"
                    ? "Male"
                    : details.gender === "F"
                      ? "Female"
                      : details.gender}
                </span>
              </div>
            )}
            <div style={rowStyle}>
              <span style={labelStyle}>Masked Aadhaar:</span>
              <span style={valueStyle}>
                {details?.maskedAadhaar || "XXXX-XXXX-1234"}
              </span>
            </div>
            {verifiedAt && (
              <div style={rowStyle}>
                <span style={labelStyle}>Verified On:</span>
                <span style={valueStyle}>
                  {new Date(verifiedAt).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        ) : isPan ? (
          <div
            style={{
              display: "grid",
              gap: 6,
              fontSize: 12,
              borderTop: "1px solid #dcfce7",
              paddingTop: 8,
            }}
          >
            <div style={rowStyle}>
              <span style={labelStyle}>Registered Name:</span>
              <span style={valueStyle}>
                {details?.registeredName ||
                  details?.legalName ||
                  "Verified Entity"}
              </span>
            </div>
            <div style={rowStyle}>
              <span style={labelStyle}>PAN:</span>
              <span style={valueStyle}>{referenceId || details?.pan}</span>
            </div>
            {details?.category && (
              <div style={rowStyle}>
                <span style={labelStyle}>Category:</span>
                <span style={valueStyle}>
                  {details.category === "P"
                    ? "Individual (Category P)"
                    : details.category === "C"
                      ? "Company (Category C)"
                      : `Category ${details.category}`}
                </span>
              </div>
            )}
            {(details?.requiresManualApproval || details?.category === "P") && (
              <div
                style={{
                  background: "#fefce8",
                  border: "1px solid #fef08a",
                  borderRadius: 6,
                  padding: "6px 10px",
                  color: "#854d0e",
                  fontSize: 11,
                  marginTop: 4,
                }}
              >
                <InlineIcon name="warning" size={12} />{" "}
                <strong>Requires Admin Approval:</strong>{" "}
                {details?.category === "P"
                  ? "Individual-category PAN detected. "
                  : ""}
                Account will be placed in the admin review queue upon
                registration.
              </div>
            )}
            {verifiedAt && (
              <div style={rowStyle}>
                <span style={labelStyle}>Verified On:</span>
                <span style={valueStyle}>
                  {new Date(verifiedAt).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gap: 6,
              fontSize: 12,
              borderTop: "1px solid #dcfce7",
              paddingTop: 8,
            }}
          >
            <div style={rowStyle}>
              <span style={labelStyle}>Legal Name:</span>
              <span style={valueStyle}>
                {details?.legalName || "Verified Business Entity"}
              </span>
            </div>
            {details?.tradeName && (
              <div style={rowStyle}>
                <span style={labelStyle}>Trade Name:</span>
                <span style={valueStyle}>{details.tradeName}</span>
              </div>
            )}
            <div style={rowStyle}>
              <span style={labelStyle}>GSTIN:</span>
              <span style={valueStyle}>{referenceId || details?.gstin}</span>
            </div>
            <div style={rowStyle}>
              <span style={labelStyle}>GST Status:</span>
              <span
                style={{ ...valueStyle, color: "#15803d", fontWeight: 700 }}
              >
                {details?.gstStatus || "ACTIVE"}
              </span>
            </div>
            {details?.registeredAddress && (
              <div style={rowStyle}>
                <span style={labelStyle}>Address:</span>
                <span style={valueStyle}>{details.registeredAddress}</span>
              </div>
            )}
            {verifiedAt && (
              <div style={rowStyle}>
                <span style={labelStyle}>Verified On:</span>
                <span style={valueStyle}>
                  {new Date(verifiedAt).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={containerStyle("failed")}>
      <div style={badgeStyle("failed")}>
        <InlineIcon name="close" size={12} /> Verification Failed
      </div>
      <div style={textStyle}>
        Identity verification could not be completed. Please try again with
        valid credentials.
      </div>
    </div>
  );
}

const containerStyle = (kind) => ({
  padding: 12,
  borderRadius: 8,
  border: `1px solid ${
    kind === "verified" ? "#86efac" : kind === "failed" ? "#fecaca" : "#fef08a"
  }`,
  background:
    kind === "verified" ? "#f0fdf4" : kind === "failed" ? "#fef2f2" : "#fefce8",
  fontFamily: font,
  margin: "10px 0",
});

const badgeStyle = (kind) => ({
  fontSize: 12,
  fontWeight: 700,
  color:
    kind === "verified" ? "#15803d" : kind === "failed" ? "#b91c1c" : "#854d0e",
});

const secureBadgeStyle = {
  fontSize: 10,
  fontWeight: 700,
  background: "#dcfce7",
  color: "#166534",
  padding: "2px 8px",
  borderRadius: 12,
  textTransform: "uppercase",
};

const textStyle = {
  fontSize: 11,
  color: C.textDark || "#1e293b",
  lineHeight: 1.5,
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 4,
};

const labelStyle = {
  color: "#475569",
  fontWeight: 500,
  flexShrink: 0,
};

const valueStyle = {
  color: "#0f172a",
  fontWeight: 600,
  wordBreak: "break-word",
};
